import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, ShieldOff, KeyRound } from 'lucide-react';
import { Button, FormSection } from '../../ui';
import { mfaApi } from '../../services/mfaApi';
import { MfaSetupWizard } from '../../components/mfa/MfaSetupWizard';
import { MfaStatusBadge } from '../../components/mfa/MfaStatusBadge';
import { ConfirmIdentityModal } from '../../components/mfa/ConfirmIdentityModal';
import { RecoveryCodesDisplay } from '../../components/mfa/RecoveryCodesDisplay';
import { useAuth } from '../../contexts/AuthContext';
import './SecuritySettingsPage.css';

const MFA_STATUS_KEY = ['auth', 'mfa', 'status'];
const RECOVERY_CODES_KEY = ['auth', 'mfa', 'recovery-codes'];

type PendingAction = 'disable_totp' | 'regenerate_codes' | null;

export function SecuritySettingsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [showWizard, setShowWizard] = useState(false);
  // When a sensitive action returns 403 step_up_required we open the modal.
  // After the user verifies, we re-run that same action.
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [latestRecoveryCodes, setLatestRecoveryCodes] = useState<string[] | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const statusQuery = useQuery({
    queryKey: MFA_STATUS_KEY,
    queryFn: () => mfaApi.status(),
  });

  const codeStatsQuery = useQuery({
    queryKey: RECOVERY_CODES_KEY,
    queryFn: () => mfaApi.recoveryCodesStatus(),
    enabled: !!statusQuery.data?.data?.mfa_enabled,
    // 403 step_up_required is a normal response on this endpoint when the
    // session is stale — don't retry, surface it via state.
    retry: false,
  });

  const disableMutation = useMutation({
    mutationFn: () => mfaApi.disableTotp(),
    onSuccess: response => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: MFA_STATUS_KEY });
        queryClient.invalidateQueries({ queryKey: RECOVERY_CODES_KEY });
        setActionError(null);
      } else if (response.step_up_required) {
        setPendingAction('disable_totp');
      } else {
        setActionError(response.error || 'Failed to disable MFA');
      }
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: () => mfaApi.regenerateRecoveryCodes(),
    onSuccess: response => {
      if (response.success && response.data) {
        setLatestRecoveryCodes(response.data.recovery_codes);
        queryClient.invalidateQueries({ queryKey: RECOVERY_CODES_KEY });
        setActionError(null);
      } else if (response.step_up_required) {
        setPendingAction('regenerate_codes');
      } else {
        setActionError(response.error || 'Failed to regenerate codes');
      }
    },
  });

  const handleStepUpConfirmed = () => {
    const action = pendingAction;
    setPendingAction(null);
    if (action === 'disable_totp') disableMutation.mutate();
    if (action === 'regenerate_codes') regenerateMutation.mutate();
  };

  const handleSetupComplete = () => {
    setShowWizard(false);
    queryClient.invalidateQueries({ queryKey: MFA_STATUS_KEY });
    queryClient.invalidateQueries({ queryKey: RECOVERY_CODES_KEY });
  };

  if (statusQuery.isLoading) {
    return <div className="security-settings__loading">Loading…</div>;
  }

  const mfaEnabled = !!statusQuery.data?.data?.mfa_enabled;
  const totpAvailable = !!statusQuery.data?.data?.totp?.enabled;
  const codeStats = codeStatsQuery.data?.success ? codeStatsQuery.data.data : null;
  // If the recovery-codes endpoint returned step_up_required we want a
  // friendly call-to-action rather than an inline error.
  const codeStatsBlocked = codeStatsQuery.data?.step_up_required === true;

  return (
    <div className="security-settings">
      <header className="security-settings__header">
        <h1>Security</h1>
        <p>Manage how you sign in to your account.</p>
      </header>

      <FormSection
        title={
          <span className="security-settings__title">
            <ShieldCheck size={18} aria-hidden="true" /> Multi-factor authentication
          </span>
        }
        description="Adds a second step to sign-in using an authenticator app, plus one-time recovery codes."
      >
        <div className="security-settings__status">
          <MfaStatusBadge
            enabled={mfaEnabled}
            required={user?.mfa_setup_required}
            overdue={user?.mfa_setup_overdue}
          />
          {user?.mfa_setup_required && !mfaEnabled && (
            <p className="security-settings__nudge">
              Your organization requires MFA.{' '}
              {user.mfa_setup_deadline ? (
                <>
                  Enroll before{' '}
                  <strong>{new Date(user.mfa_setup_deadline).toLocaleString()}</strong> to keep
                  signing in without interruption.
                </>
              ) : (
                <>Enroll soon to avoid being blocked at your next sign-in.</>
              )}
            </p>
          )}
        </div>

        {!showWizard && !mfaEnabled && (
          <Button type="button" onClick={() => setShowWizard(true)}>
            Set up authenticator app
          </Button>
        )}

        {showWizard && (
          <MfaSetupWizard onComplete={handleSetupComplete} onCancel={() => setShowWizard(false)} />
        )}

        {mfaEnabled && !showWizard && (
          <>
            <div className="security-settings__totp-row">
              <span>
                Authenticator app: <strong>enabled</strong>
              </span>
              <Button
                type="button"
                variant="secondary"
                onClick={() => disableMutation.mutate()}
                loading={disableMutation.isPending}
              >
                <ShieldOff size={16} aria-hidden="true" /> Turn off
              </Button>
            </div>
            {actionError && <p className="security-settings__error">{actionError}</p>}
          </>
        )}
      </FormSection>

      {mfaEnabled && (
        <FormSection
          title={
            <span className="security-settings__title">
              <KeyRound size={18} aria-hidden="true" /> Recovery codes
            </span>
          }
          description="One-time backup codes if you lose access to your authenticator app."
        >
          {codeStatsBlocked ? (
            <p className="security-settings__nudge">
              View recovery codes is a sensitive action. We&apos;ll ask you to confirm your identity
              before showing them.
            </p>
          ) : codeStats ? (
            <p>
              <strong>{codeStats.remaining_codes}</strong> of {codeStats.total_codes} codes
              remaining ({codeStats.used_codes} used).
            </p>
          ) : null}

          <Button
            type="button"
            variant="secondary"
            onClick={() => regenerateMutation.mutate()}
            loading={regenerateMutation.isPending}
          >
            Regenerate recovery codes
          </Button>

          {latestRecoveryCodes && (
            <RecoveryCodesDisplay
              codes={latestRecoveryCodes}
              warning="Save these new codes — they replace any previous codes."
              onAcknowledge={() => setLatestRecoveryCodes(null)}
            />
          )}
        </FormSection>
      )}

      <ConfirmIdentityModal
        open={pendingAction !== null}
        totpAvailable={totpAvailable}
        title={
          pendingAction === 'disable_totp'
            ? 'Confirm to turn off MFA'
            : 'Confirm to view recovery codes'
        }
        description="For your safety we need to confirm it's you before continuing."
        onConfirmed={handleStepUpConfirmed}
        onCancel={() => setPendingAction(null)}
      />
    </div>
  );
}
